/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosButton, EdsIcon } from "@components/elements/atoms";
import { useAutoStartDownload } from "@components/hooks";
import { useOsName } from "@components/hooks/userAgent.hooks";
import { CosmosStackProps } from "@components/types";
import {
	CosmosEnumEdsGradients,
	CosmosPluginDownloadDetailFragment,
	CosmosTitleAndDescFragment,
} from "@cosmos/hygraph-schema";
import { Option, Select } from "@eds-web/react";
import { useMessages } from "@epic-mw/i18n";
import { css, cx } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TitleDescStack } from "../title-desc-stack";
import { ColorfulTitle } from "../title-desc-stack/ColorfulTitle.molecule";

type DownloadOption<T extends string> = Option<T> & {
	os?: string;
};

export interface GetLatestCardProps {
	sections?: CosmosTitleAndDescFragment[] | null;
	downloads?: CosmosPluginDownloadDetailFragment[] | null;
	downloadName?: string | null;
	overrideDownloadUrl?: string | null;
	osName?: string;
	onOsNameChange?: (os: string) => void;
}

const styles = css({
	borderRadius: "eds.borderRadius.24",
	border: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.005)",
	backdropFilter: "blur(75px)",
	background: "eds.palette.transparent.light.005",
	maxWidth: "cosmos.article.sm",
});

const sectionStyles = css({
	padding: "eds.dimension.32",
});

const latestStyles = css({
	borderBlockStart: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.005)",
	paddingBlock: "eds.dimension.24",
	paddingInline: "eds.dimension.32",
	borderBottomRadius: "eds.borderRadius.24",
	background: "eds.palette.transparent.dark.015",
});

export const GetLatestCard = ({
	sections = [],
	downloads = [],
	downloadName,
	overrideDownloadUrl,
	osName: providedOsName,
	onOsNameChange,
}: GetLatestCardProps): React.JSX.Element | null => {
	const messages = useMessages();
	const detectedOsName = useOsName();

	const [osName, setOsName] = useState(providedOsName);
	const [prestine, setPrestine] = useState(true);

	const handleOsChange = useCallback(
		(newOs: string) => {
			if (onOsNameChange) onOsNameChange(newOs);
			setOsName(newOs);
		},
		[onOsNameChange],
	);

	useEffect(() => {
		if (prestine && detectedOsName) {
			setPrestine(false);
			if (detectedOsName !== osName) setOsName(detectedOsName);
		} else if (typeof providedOsName === "string" && providedOsName !== osName) {
			handleOsChange(providedOsName);
		}
	}, [osName, handleOsChange, providedOsName, prestine, detectedOsName]);

	const validDownloads = useMemo(
		() => (downloads || [])?.filter((d) => d.downloadUrl && d.operatingSystem),
		[downloads],
	);

	const downloadMap = useMemo(() => {
		const map = new Map<string, CosmosPluginDownloadDetailFragment>();
		(validDownloads || []).forEach((d) => {
			if (d.downloadUrl && d.operatingSystem) {
				map.set(d.downloadUrl, d);
			}
		});
		return map;
	}, [validDownloads]);

	const validLabels: DownloadOption<string>[] = useMemo(
		() =>
			validDownloads?.map((d) => ({
				label: messages[`epic.cosmos.os.${d.operatingSystem}`] || "",
				value: d.downloadUrl || "",
				os: d.operatingSystem?.toLowerCase(),
			})),
		[messages, validDownloads],
	);

	const findBestOption = useCallback(
		(targetOs?: string): DownloadOption<string> | undefined => {
			if (!validLabels.length) return undefined;

			if (targetOs) {
				const match = validLabels.find((label) => label.os === targetOs.toLowerCase());
				if (match) return match;
			}

			// Fallback to Windows if available
			const windowsMatch = validLabels.find((label) => label.os === "windows");
			if (windowsMatch) return windowsMatch;

			// Fallback to first available
			return validLabels[0];
		},
		[validLabels],
	);

	const [selected, setSelected] = useState<DownloadOption<string> | undefined>(() => findBestOption(osName));

	useEffect(() => {
		const matchedOption = findBestOption(osName);
		if (matchedOption) {
			setSelected(matchedOption);
		}
	}, [osName, findBestOption]);

	const router = useRouter();
	const pathname = usePathname();

	const searchParams = useSearchParams();
	const disableAutoStart = Boolean(overrideDownloadUrl);
	const download = useAutoStartDownload(
		disableAutoStart || !selected?.value ? "" : selected?.value,
		downloadName || "",
	);

	useEffect(() => {
		if (!disableAutoStart && searchParams && searchParams.has("start") && selected?.value) {
			download();
			const newSearchParams = new URLSearchParams(searchParams.toString());
			newSearchParams.delete("start");
			const pathParams = pathname + "?" + newSearchParams.toString();
			router.replace(pathParams);
		}
	}, [disableAutoStart, download, pathname, router, searchParams, selected?.value]);

	const onChange = useCallback(
		(option: Option<string>) => {
			setSelected(option);
			const os = downloadMap.get(option.value)?.operatingSystem;
			if (os) {
				handleOsChange(os.toLowerCase());
			}
		},
		[downloadMap, handleOsChange],
	);

	if (!sections?.length) return null;

	const stack: CosmosStackProps = { gap: "eds.dimension.2" };

	return (
		<div className={cx("get-latest-card", styles)}>
			<VStack gap="eds.dimension.24" alignItems="flex-start" className={sectionStyles}>
				{sections.map((s, i) => (
					<TitleDescStack
						key={`latest-card-section=${i}`}
						stack={stack}
						title={{ ...s.title, variant: "paragraphLGBold" }}
						description={{ html: s.desc, variant: "paragraphSM" }}
					/>
				))}
			</VStack>
			{validDownloads?.length ? (
				<section className={cx("latest", latestStyles)}>
					<HStack justifyContent="space-between" flexWrap="wrap">
						<HStack gap="eds.dimension.16">
							<EdsIcon icon="ArrowDownCircleFilled" gradient="blue" />
							<ColorfulTitle
								gradient={CosmosEnumEdsGradients.Blue}
								title={{
									html: `<span title=${downloadName || ""}>${messages["epic.cosmos.plugins.latest.version"]}</span>`,
									variant: "paragraphLGBold",
								}}
							/>
						</HStack>
						<HStack gap="eds.dimension.16" alignItems="flex-end" flexWrap="wrap">
							{validLabels?.length > 1 ? (
								<div className={css({ minWidth: "180px" })}>
									<Select
										label={messages["epic.cosmos.os"]}
										clearable={false}
										value={selected}
										placeholder={messages["epic.cosmos.os"]}
										options={validLabels}
										onChange={onChange}
									></Select>
								</div>
							) : null}
							<CosmosButton
								variant="primary"
								label={messages["epic.cosmos.plugins.download"]}
								href={overrideDownloadUrl || selected?.value}
								name={downloadName}
								title={(!overrideDownloadUrl && downloadName) || ""}
								size="lg"
							/>
						</HStack>
					</HStack>
				</section>
			) : null}
		</div>
	);
};
